import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart } from "lucide-react";
import { inventoryApi, inventoryCategoryApi } from "#/features/inventory/api";
import { useAsyncData, useDebouncedValue, usePageState } from "#/features/inventory/hooks";

/** Availability shown to ordinary staff — a status, not the officer-level exact stock
 *  figures (those stay on the Inventory module's own Items page). */
function AvailabilityBadge({ belowReorderLevel, currentStockUnits }: { belowReorderLevel: boolean; currentStockUnits: number }) {
  if (currentStockUnits <= 0) {
    return <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">Out of stock</span>;
  }
  if (belowReorderLevel) {
    return <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Low stock</span>;
  }
  return <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">In stock</span>;
}

/** Staff-facing item catalog — browse what the store carries and jump straight into a
 *  pre-filled Staff Requisition for a specific item. Unlike the Inventory module's own
 *  Items page (officer-only, exact stock counts), this shows only an availability status
 *  and is open to anyone who can submit a Staff Requisition. */
export default function RequisitionCatalogPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const debouncedQ = useDebouncedValue(q);
  const { page, size, setPage, setSize } = usePageState(12);

  const categories = useAsyncData(() => inventoryCategoryApi.list({ active: true }), []);

  const { data, loading, error } = useAsyncData(
    () => inventoryApi.listItems({ q: debouncedQ, page, size, categoryId, status: "ACTIVE" }),
    [debouncedQ, page, size, categoryId],
  );

  function requestItem(itemId: number) {
    navigate(`/hr/requisitions/new?itemId=${itemId}`);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Request Items</h1>
        </div>
        <Link to="/hr/requisitions/mine" className="text-sm font-semibold text-emerald-700 hover:underline">
          My Requisitions &rarr;
        </Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            className="input pl-9"
            placeholder="Search by code or name..."
            value={q}
            onChange={(e) => { setPage(0); setQ(e.target.value); }}
          />
        </div>
        <select
          className="input w-48"
          value={categoryId}
          onChange={(e) => { setPage(0); setCategoryId(e.target.value ? Number(e.target.value) : ""); }}
        >
          <option value="">All Categories</option>
          {(categories.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading && <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">Loading items...</div>}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Availability</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.content ?? []).map((item, idx) => (
                <tr key={item.id} className={idx % 2 === 1 ? "bg-slate-50/60" : "bg-white"}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{item.code}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                  <td className="px-4 py-3 text-slate-500">{item.categoryName || "-"}</td>
                  <td className="px-4 py-3">
                    <AvailabilityBadge belowReorderLevel={item.belowReorderLevel} currentStockUnits={item.currentStockUnits} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => requestItem(item.id)}
                      disabled={item.currentStockUnits <= 0}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ShoppingCart className="h-3.5 w-3.5" />
                      Request
                    </button>
                  </td>
                </tr>
              ))}
              {(data?.content ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No items found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">
            {data ? `${data.totalElements.toLocaleString()} total — Page ${data.number + 1} of ${Math.max(1, data.totalPages)}` : ""}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={!data || data.first}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!data || data.last}
              className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
